import moment from 'moment';
import { Dispatch, SetStateAction } from 'react';
import { UseFieldArrayReturn, UseFormReturn } from 'react-hook-form';

import { DynamicTableCellConfig } from '~/legacy-components/LegacyDynamicFormTable/types';
import { getAttachmentUrl } from '~/utils/helper';

import { IFormInput } from '../useEquipmentTab';
import { RowData } from '../useEquipmentTab';

import { EquipListType, Equipment, EquipmentType } from '.';

export const readData = (
  companyEquip: EquipmentType[],
  empId: string,
  equipEnumType: Equipment[],
) =>
  companyEquip &&
  companyEquip.map((equip) => {
    const type = equipEnumType.find(
      (equipEnum) => equipEnum.code === equip.fEquipmentType,
    );
    return [
      type ? type.name : '',
      equip.fEquipmentItem,
      equip.fEquipmentInvNumber,
      moment(equip.fEquipmentDeliveryDate).format('DD.MM.YYYY'),
      equip.fEquipmentReturnDate
        ? moment(equip.fEquipmentReturnDate).format('DD.MM.YYYY')
        : '',
      equip.fEquipmentInfo ? equip.fEquipmentInfo : '',
      equip.fEquipmentAttachmentName
        ? {
            hrefLink: getAttachmentUrl(empId, equip.fEquipmentAttachmentId),
            name: equip.fEquipmentAttachmentName,
          }
        : '',
      equip.fEquipmentExpirationDate
        ? moment(equip.fEquipmentExpirationDate).format('DD.MM.YYYY')
        : '',
      '',
    ];
  });

export const mappedData = (
  useFieldArrayMethods: UseFieldArrayReturn<IFormInput, 'equipment', 'id'>,
  useFormMethods: UseFormReturn<IFormInput>,
  equipList: EquipListType[],
  setDeletedRows: Dispatch<SetStateAction<number[]>>,
  getCellConfig: (
    cellName: string,
    index: number,
    equipListValue: EquipListType | undefined,
    bnNewCompanyItem: boolean | undefined,
    fEquipmentId?: number,
    value?: string | number,
  ) => DynamicTableCellConfig,
) => {
  const { fields, remove } = useFieldArrayMethods;
  const { getValues, register, setValue } = useFormMethods;
  const editData: DynamicTableCellConfig[][] = fields.map((equip, index) => {
    const equipmentValues = getValues();
    const equipDataValue =
      equipmentValues && equipmentValues.equipment
        ? equipmentValues.equipment[index]
        : ({} as RowData);
    const findEquipList = equipList.find((el) =>
      equipDataValue && equipDataValue.fEquipmentCompEqFK
        ? el.fComEquipmentId === equipDataValue.fEquipmentCompEqFK
        : el.fComEquipmentId === equip.fEquipmentCompEqFK,
    );

    const cellConfig = (cellName: string) =>
      getCellConfig(
        cellName,
        index,
        findEquipList,
        equip.bnNewCompanyItem,
        equip.fEquipmentId,
        (equip as RowData)[cellName] as string | number | undefined,
      );

    register(`equipment.${index}.fEquipmentAttachment`);
    register(`equipment.${index}.fEquipmentId`);
    register(`equipment.${index}.fEquipmentCompEqFK`);
    if (equip.bnNewCompanyItem === undefined) {
      setValue(`equipment.${index}.fEquipmentId`, equip.fEquipmentId);
      setValue(
        `equipment.${index}.fEquipmentCompEqFK`,
        equip.fEquipmentCompEqFK,
      );
    }
    return [
      { key: equip.id, ...cellConfig('fEquipmentType') },
      {
        ...cellConfig('fEquipmentItem'),
      },
      {
        ...cellConfig('fEquipmentInvNumber'),
      },
      {
        ...cellConfig('fEquipmentDeliveryDate'),
      },
      {
        ...cellConfig('fEquipmentReturnDate'),
      },
      {
        ...cellConfig('fEquipmentInfo'),
      },
      {
        ...cellConfig('fEquipmentAttachmentName'),
      },
      {
        ...cellConfig('fEquipmentExpirationDate'),
      },
      {
        type: 'delete',
        width: '20px',
        onChange: (deleteIndex: number) => {
          remove(deleteIndex);
          if (equip && equip.fEquipmentId) {
            setDeletedRows((prevState) => [
              ...prevState,
              equip?.fEquipmentId || -1,
            ]);
          }
        },
      },
    ];
  });

  return {
    editData,
  };
};

export const mapDefaultData = (companyEquip: EquipmentType[]): RowData[] =>
  companyEquip.map((equip) => ({
    fEquipmentId: equip.fEquipmentId,
    fEquipmentCompEqFK: equip.fEquipmentCompEqFK,
    fEquipmentType: equip.fEquipmentType || '',
    fEquipmentItem: equip.fEquipmentItem || '',
    fEquipmentInvNumber: equip.fEquipmentInvNumber || '',
    fEquipmentDeliveryDate: equip.fEquipmentDeliveryDate || null,
    fEquipmentAttachmentId: equip.fEquipmentAttachmentId,
    fEquipmentAttachmentName: equip.fEquipmentAttachmentName || '',
    fEquipmentAttachment: undefined,
    fEquipmentReturnDate: equip.fEquipmentReturnDate || null,
    fEquipmentInfo: equip.fEquipmentInfo || '',
    fEquipmentExpirationDate: equip.fEquipmentExpirationDate || null,
  }));
